/**
 * The app lock: a PIN (or a biometric, where the platform can do it properly)
 * that stands in for re-typing the passphrase every session.
 *
 * WHY THE PASSPHRASE AND NOT THE DERIVED KEY. Caching the derived key would be
 * the obvious design and does not work here: every push writes a fresh random
 * salt into the envelope (crypto.ts), so the key is re-derived per snapshot and
 * a cached one would be stale the moment the laptop syncs. The passphrase is
 * the only thing worth keeping — and it is kept ENCRYPTED, never raw, which is
 * what CLAUDE.md §10 requires.
 *
 * WHY A PIN IS NOT WEAK HERE. Four digits is 10,000 possibilities, which any
 * attacker holding the device could grind. Two things stand in the way:
 *   1. PBKDF2 at the same 310,000 iterations as the main envelope, so each
 *      guess costs real time rather than a hash;
 *   2. a wipe after MAX_ATTEMPTS failures — the wrapped passphrase is deleted,
 *      not merely rejected, so the grind ends after a handful of tries.
 * The wipe costs the legitimate user nothing they cannot recover: their books
 * are re-downloadable and the passphrase still opens them.
 *
 * WHAT THIS IS NOT. It is not a second factor and not a second secret. Anyone
 * who knows the passphrase does not need the PIN. It exists to make daily use
 * bearable without ever writing the passphrase down in the clear.
 */
import {
  DecryptionError,
  IV_BYTES,
  KDF_ITERATIONS_DEFAULT,
  SALT_BYTES,
  aesGcmDecrypt,
  aesGcmEncrypt,
  deriveKey,
  randomBytes,
  utf8,
} from "@tally/shared";

import { deleteSetting, getSetting, setSetting } from "./sync/db.js";

/** Deleting beats locking out: the passphrase is always a way back in. */
export const MAX_ATTEMPTS = 8;

/** Below this a PIN is guessable by hand, before any cryptography is involved. */
export const MIN_PIN_LENGTH = 4;

const KEY = "lock:v1";

type Wrapped = {
  /** base64 — the PBKDF2 salt for the PIN, distinct from any envelope salt */
  salt: string;
  iv: string;
  ciphertext: string;
  iterations: number;
  /** counts DOWN, so a corrupted record fails closed rather than open */
  attemptsLeft: number;
  /** "pin" | "biometric" — what the user set up, for wording in the UI */
  method: LockMethod;
  createdAt: string;
};

export type LockMethod = "pin" | "biometric";

export type LockState =
  | { kind: "none" }
  | { kind: "set"; method: LockMethod; attemptsLeft: number };

const b64 = {
  encode: (bytes: Uint8Array): string => btoa(String.fromCharCode(...bytes)),
  decode: (text: string): Uint8Array =>
    Uint8Array.from(atob(text), (character) => character.charCodeAt(0)),
};

export async function lockState(): Promise<LockState> {
  const wrapped = await getSetting<Wrapped | null>(KEY, null);
  if (!wrapped) return { kind: "none" };
  return { kind: "set", method: wrapped.method, attemptsLeft: wrapped.attemptsLeft };
}

/**
 * Store the passphrase, encrypted under the PIN.
 *
 * Called only from a session that has ALREADY unlocked with the passphrase, so
 * the value being wrapped is known-good — we never wrap a passphrase that has
 * not decrypted a real snapshot, which would create a lock that opens onto an
 * error.
 */
export async function setLock(
  pin: string,
  passphrase: string,
  method: LockMethod = "pin",
): Promise<void> {
  if (pin.length < MIN_PIN_LENGTH) {
    throw new Error(`PIN must be at least ${MIN_PIN_LENGTH} digits`);
  }

  const salt = randomBytes(SALT_BYTES);
  const iv = randomBytes(IV_BYTES);
  const key = await deriveKey(pin, salt, KDF_ITERATIONS_DEFAULT);
  const ciphertext = await aesGcmEncrypt(key, iv, utf8.encode(passphrase));

  const wrapped: Wrapped = {
    salt: b64.encode(salt),
    iv: b64.encode(iv),
    ciphertext: b64.encode(ciphertext),
    iterations: KDF_ITERATIONS_DEFAULT,
    attemptsLeft: MAX_ATTEMPTS,
    method,
    createdAt: new Date().toISOString(),
  };

  await setSetting(KEY, wrapped);
}

export async function clearLock(): Promise<void> {
  await deleteSetting(KEY);
}

export type UnlockResult =
  | { ok: true; passphrase: string }
  | { ok: false; reason: "wrong"; attemptsLeft: number }
  | { ok: false; reason: "wiped" }
  | { ok: false; reason: "none" };

/**
 * Try a PIN.
 *
 * The attempt is decremented BEFORE the expensive derivation and restored only
 * on success. Decrementing afterwards would let an attacker kill the app mid-
 * derivation and retry indefinitely at no cost to the counter.
 */
export async function unlockWithPin(pin: string): Promise<UnlockResult> {
  const wrapped = await getSetting<Wrapped | null>(KEY, null);
  if (!wrapped) return { ok: false, reason: "none" };

  const remaining = wrapped.attemptsLeft - 1;
  if (remaining < 0) {
    await clearLock();
    return { ok: false, reason: "wiped" };
  }
  await setSetting(KEY, { ...wrapped, attemptsLeft: remaining });

  try {
    const key = await deriveKey(pin, b64.decode(wrapped.salt), wrapped.iterations);
    const plain = await aesGcmDecrypt(key, b64.decode(wrapped.iv), b64.decode(wrapped.ciphertext));

    await setSetting(KEY, { ...wrapped, attemptsLeft: MAX_ATTEMPTS });
    return { ok: true, passphrase: utf8.decode(plain) };
  } catch (error) {
    if (!(error instanceof DecryptionError)) throw error;

    if (remaining === 0) {
      // Out of tries. Delete rather than block: a wrapped passphrase nobody can
      // open is only a liability, and the real passphrase still works.
      await clearLock();
      return { ok: false, reason: "wiped" };
    }
    return { ok: false, reason: "wrong", attemptsLeft: remaining };
  }
}

/* ────────────────────────────────────────────────────────────── biometric ── */

/**
 * Biometrics are offered ONLY where the platform can produce a key from them —
 * WebAuthn's PRF extension.
 *
 * The tempting alternative is to treat a successful fingerprint prompt as
 * permission to read an unwrapped passphrase off disk. That is theatre: the
 * passphrase would be sitting in IndexedDB in the clear, and anyone reading the
 * database directly would skip the prompt entirely. Better to not offer the
 * option than to offer a lock with no lock in it.
 */
export async function biometricAvailable(): Promise<boolean> {
  if (typeof PublicKeyCredential === "undefined") return false;
  try {
    const platform = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    if (!platform) return false;
    // PRF is what turns an authenticator into a key source. Without it we can
    // authenticate the user but cannot derive anything to encrypt with.
    return typeof PublicKeyCredential.getClientCapabilities === "function"
      ? Boolean((await PublicKeyCredential.getClientCapabilities())["extension:prf"])
      : false;
  } catch {
    return false;
  }
}
